import React, { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Column, Small, Table } from "@fronterahealth/frontera-ui-components";

import { CaregiverType, ProviderType, useCaregiverByIdQuery } from "@api/graphql/types-and-hooks";
import { LinkedChild } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useProvidersData } from "@providers/ProvidersDataProvider";

const childrenColumns: Column<LinkedChild>[] = [
  {
    title: "Child Name",
    dataIndex: "name",
    key: "name",
  },
  {
    title: "Date of Birth",
    dataIndex: "dateOfBirth",
    key: "dateOfBirth",
  },
  {
    title: "Relationship",
    dataIndex: "relationship",
    key: "relationship",
  },
  {
    title: "Assigned BCBA",
    dataIndex: "assignedBcba",
    key: "assignedBcba",
  },
];

export const CaregiverChildrenTab: React.FC = () => {
  const { caregiverId } = useParams<{ caregiverId: string }>();

  const { providersQuery } = useProvidersData();

  const caregiverDetailsQuery = useCaregiverByIdQuery(
    { caregiverId: caregiverId || "" },
    {
      queryKey: ["caregiver-by-id", caregiverId],
      enabled: !!caregiverId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregiver = mapCaregiverToMemberCaregiver(
    caregiverDetailsQuery.data?.caregiverById as CaregiverType | undefined,
    bcbaNameByProviderId,
  );

  if (!caregiver) {
    if (caregiverDetailsQuery.isFetching) return null;
    return <div className="text-text-secondary">Caregiver not found.</div>;
  }

  if (caregiver.linkedChildren.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Small>No children linked</Small>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Table
        cellClassName="bg-white"
        inputClassName="bg-white"
        data={caregiver.linkedChildren}
        columns={childrenColumns}
        loading={false}
        emptyMessage="No children linked"
      />
    </div>
  );
};
